import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * Fair day (El pozo 3, docs/SAGAS.md): Musgoviejo's square on the night of its first fair in forty years. The well in the
 * middle is shut, the millstone lying flat on it, and a pole through its eye carries the bunting out to the stalls. Round
 * it, on the second ring, the stalls (`rules.fair.stalls`): striped awnings, a counter, a lantern hanging at the front;
 * the first (`rules.fair.cart`) is Fenn's cart, a pedlar's wagon hung with pots. The lanterns' light matters: the ink
 * thieves show only within it (`rules.fair.light`, and `rules.fair.lantern` round a hero), so each stall throws a warm
 * pool on the flags with a faint rim where its light ends, and each hero a smaller one.
 *
 * The wares (`s.fair.wares`): small goods on their stall's counter while there (a stall that lost its wares is bare),
 * dragged at a dragger's heels, lying on the ground with a cold ring round them while they wait to be picked up, held
 * over a hero's head on the way back, gone when lost down a hole. At the close every lantern flares.
 *
 * Draw calls: the square (well, stalls, cart, bunting; one merged mesh), the lit lanterns (one), the pools' rims (one),
 * the wares (one instanced mesh per shape), the cold rings under loose wares (one), a glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['fair']>;

const K = WORLD_PER_HEX / 1000;
const WOOD = '#4a372b';
const WOOD_DARK = '#33261d';
const PLANK = '#5a4130';
const STONE = ['#6f7178', '#62646b', '#7a7c82'];
const MILLSTONE = '#8a8780';
const IRON = '#2f3137';
const LAMP = new THREE.Color('#ffd6a0');
const LAMP_HOT = new THREE.Color('#ffe9c8');
const COOL = new THREE.Color('#cfe8ff');
/** Awning stripes, a pair per stall (deep, not gold): red, teal, slate blue, moss, plum. */
const STRIPES: [string, string][] = [
  ['#9a3a30', '#d9d2c6'],
  ['#2f6a6a', '#d6d8d2'],
  ['#3a4a7a', '#cfd2d8'],
  ['#4f6a3a', '#d4d6cc'],
  ['#6a3a5a', '#d8d0d4'],
];
const FLAGS = ['#ff4f3a', '#cfe8ff', '#4f8a8a', '#d9d2c6', '#6a7ab0'];
/** The wares' shapes: crate, sack, jar, roll of cloth, cheese wheel. Fenn's are pots (jars). */
type Shape = 0 | 1 | 2 | 3 | 4;
const WARE_COLORS: Record<Shape, string[]> = {
  0: ['#9a7048', '#86603e'],
  1: ['#d6ccb4', '#c8bea6'],
  2: ['#b0704e', '#6f86a0'],
  3: ['#b04a5a', '#4a78a8', '#6a9a4a'],
  4: ['#e0b860', '#d8a850'],
};

export class FairLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  /** Each stall: where it stands (world), its yaw, its lantern, its counter slots (world) for its wares. */
  private stalls: { p: THREE.Vector3; lamp: THREE.Vector3; slots: THREE.Vector3[] }[] = [];
  /** Each ware: its shape, its instance in that shape's mesh, its counter slot. */
  private wares: { shape: Shape; i: number; slot: THREE.Vector3 }[] = [];
  private meshes: THREE.InstancedMesh[] = [];
  private rings: THREE.InstancedMesh;
  private ringMat: THREE.MeshBasicMaterial;
  private light: number;
  private lantern: number;
  private flaredAt = -1;
  private now = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private sv = new THREE.Vector3();
  private zero = new THREE.Matrix4().makeScale(0, 0, 0);
  private e = new THREE.Euler();

  constructor(
    private ctx: OmenCtx,
    private r: Rules,
  ) {
    this.group.name = 'fair-square';
    this.light = r.light * K;
    this.lantern = r.lantern * K;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    const ringG = new THREE.CylinderGeometry(0.5, 0.5, 1, 14, 1, true);
    const flag = new THREE.CylinderGeometry(0.5, 0, 1, 3, 1).rotateZ(Math.PI).scale(1, 1, 0.12);

    const kit = new Kit();
    const lit = new Kit();
    // The shut well and its bunting pole.
    const g0 = ctx.ground(0, 0);
    kit.add(ringG, STONE[0] as string, place(0, g0 + 0.2, 0, 0, new THREE.Vector3(0.9, 0.4, 0.9)));
    kit.add(ringG, STONE[1] as string, place(0, g0 + 0.2, 0, 0, new THREE.Vector3(0.72, 0.4, 0.72)));
    kit.add(new THREE.TorusGeometry(0.41, 0.06, 5, 14).rotateX(Math.PI / 2), STONE[2] as string, place(0, g0 + 0.41, 0));
    kit.add(new THREE.CylinderGeometry(0.5, 0.5, 0.22, 18), MILLSTONE, place(0, g0 + 0.52, 0));
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      kit.add(box, '#6a6862', place(Math.cos(a) * 0.28, g0 + 0.635, Math.sin(a) * 0.28, -a, new THREE.Vector3(0.36, 0.01, 0.03)));
    }
    const top = new THREE.Vector3(0, g0 + 2.2, 0);
    kit.add(box, WOOD, place(0, g0 + 1.4, 0, 0, new THREE.Vector3(0.08, 1.6, 0.08)));
    kit.add(new THREE.SphereGeometry(0.07, 8, 6), '#ff4f3a', place(0, top.y + 0.05, 0));

    // The stalls round the well.
    const tmpV = new THREE.Vector3();
    r.stalls.forEach((s, i) => {
      const p = ctx.at(s.x, s.y, 0);
      const yaw = Math.atan2(-p.x, -p.z);
      const g = place(p.x, p.y, p.z, yaw, 1);
      const slots: THREE.Vector3[] = [];
      let lampLocal: THREE.Vector3;
      let anchor: THREE.Vector3;
      if (i === r.cart) {
        // Fenn's cart: a wagon bed on two big wheels, a canvas hoop over it, shafts to the front, pots hung off the side.
        kit.add(box, PLANK, g, place(0, 0.5, 0, 0, new THREE.Vector3(1.1, 0.12, 0.7)));
        for (const z of [-0.36, 0.36]) kit.add(box, WOOD_DARK, g, place(0, 0.66, z, 0, new THREE.Vector3(1.1, 0.22, 0.04)));
        kit.add(box, WOOD_DARK, g, place(-0.55, 0.66, 0, 0, new THREE.Vector3(0.04, 0.22, 0.7)));
        for (const z of [-0.42, 0.42]) {
          kit.add(new THREE.TorusGeometry(0.32, 0.035, 5, 14), WOOD_DARK, g, place(-0.1, 0.36, z));
          for (let k = 0; k < 6; k++) kit.add(box, WOOD, g, place(-0.1, 0.36, z, 0, new THREE.Vector3(0.025, 0.6, 0.025), 0, (k / 6) * Math.PI));
          kit.add(cyl, IRON, g, place(-0.1, 0.36, z, 0, new THREE.Vector3(0.09, 0.06, 0.09), Math.PI / 2, 0));
        }
        kit.add(new THREE.CylinderGeometry(0.42, 0.42, 1.0, 10, 1, true, 0, Math.PI).rotateZ(Math.PI / 2).rotateX(Math.PI / 2), '#5a6a5a', g, place(-0.1, 0.62, 0, 0, new THREE.Vector3(1, 1, 0.95)));
        for (const x of [-0.6, 0.4]) kit.add(new THREE.TorusGeometry(0.42, 0.02, 4, 10, Math.PI), WOOD_DARK, g, place(x, 0.62, 0, Math.PI / 2));
        for (const z of [-0.22, 0.22]) kit.add(box, WOOD, g, place(0.85, 0.42, z, 0, new THREE.Vector3(0.7, 0.04, 0.04), 0, -0.2));
        // Pots and a kettle hung off the near side, a bundle of brooms.
        for (let k = 0; k < 4; k++) kit.add(cyl, k % 2 ? '#5a6a7a' : '#7a5a42', g, place(-0.45 + k * 0.22, 0.6, 0.42, 0, new THREE.Vector3(0.12, 0.1, 0.12)));
        kit.add(box, '#9a8566', g, place(0.42, 0.75, -0.3, 0, new THREE.Vector3(0.05, 0.5, 0.05), 0, 0.3));
        lampLocal = new THREE.Vector3(0.6, 0.95, 0.3);
        kit.add(box, WOOD_DARK, g, place(0.6, 0.62, 0.3, 0, new THREE.Vector3(0.03, 0.66, 0.03)));
        lit.add(box, LAMP, g, place(lampLocal.x, lampLocal.y, lampLocal.z, 0, new THREE.Vector3(0.08, 0.12, 0.08)));
        // The wares set out on a plank on two barrels in front of the wagon, toward the well, where they can be seen.
        for (const x of [-0.35, 0.35]) kit.add(cyl, '#5a4130', g, place(x, 0.13, 0.78, 0, new THREE.Vector3(0.16, 0.26, 0.16)));
        kit.add(box, PLANK, g, place(0, 0.28, 0.78, 0, new THREE.Vector3(0.95, 0.04, 0.28)));
        for (let k = 0; k < 2; k++) slots.push(new THREE.Vector3(-0.22 + k * 0.44, 0.3, 0.78).applyMatrix4(g));
        anchor = new THREE.Vector3(-0.1, 1.05, 0).applyMatrix4(g);
      } else {
        const [deep, pale] = STRIPES[i % STRIPES.length] as [string, string];
        // Four posts, the counter at the front, a back shelf, a striped awning sloping toward the well.
        for (const x of [-0.5, 0.5]) for (const z of [-0.32, 0.32]) kit.add(box, WOOD, g, place(x, 0.55, z, 0, new THREE.Vector3(0.06, 1.1 + (z > 0 ? -0.15 : 0), 0.06)));
        kit.add(box, PLANK, g, place(0, 0.5, 0.2, 0, new THREE.Vector3(1.08, 0.08, 0.3)));
        kit.add(box, WOOD_DARK, g, place(0, 0.27, 0.28, 0, new THREE.Vector3(1.04, 0.46, 0.04)));
        kit.add(box, PLANK, g, place(0, 0.6, -0.3, 0, new THREE.Vector3(1.0, 0.05, 0.16)));
        // The awning, tipped back from the front so the counter and the goods in front of it show from above.
        for (let k = 0; k < 6; k++) kit.add(box, k % 2 ? pale : deep, g, place(-0.5 + 0.0833 + k * 0.1667, 1.0, -0.08, 0, new THREE.Vector3(0.1667, 0.035, 0.72), 0.32, 0));
        // The valance: a scalloped edge of the deep stripe along the front.
        for (let k = 0; k < 6; k++) kit.add(flag, deep, g, place(-0.45 + k * 0.18, 0.84, 0.26, 0, new THREE.Vector3(0.16, 0.12, 1)));
        // A low trestle out in front, where the goods are set out under the lantern.
        for (const x of [-0.42, 0.42]) kit.add(box, WOOD_DARK, g, place(x, 0.12, 0.72, 0, new THREE.Vector3(0.05, 0.24, 0.24)));
        kit.add(box, PLANK, g, place(0, 0.26, 0.72, 0, new THREE.Vector3(1.0, 0.04, 0.3)));
        // Crates under the counter, a basket on the back shelf.
        kit.add(box, '#6a4e34', g, place(-0.3, 0.1, 0.05, 0.2, 0.2));
        kit.add(cyl, '#8a7a5a', g, place(0.25, 0.68, -0.3, 0, new THREE.Vector3(0.18, 0.12, 0.18)));
        lampLocal = new THREE.Vector3(0.5, 0.74, 0.3);
        kit.add(box, IRON, g, place(0.5, 0.83, 0.3, 0, new THREE.Vector3(0.012, 0.08, 0.012)));
        lit.add(box, LAMP, g, place(lampLocal.x, lampLocal.y, lampLocal.z, 0, new THREE.Vector3(0.08, 0.11, 0.08)));
        for (let k = 0; k < 2; k++) slots.push(new THREE.Vector3(-0.24 + k * 0.48, 0.28, 0.72).applyMatrix4(g));
        anchor = new THREE.Vector3(0, 1.12, 0.1).applyMatrix4(g);
      }
      const lamp = lampLocal.clone().applyMatrix4(g);
      this.stalls.push({ p, lamp, slots });
      // A string of bunting from the pole over the well to the stall, sagging, its pennants in turn.
      const n = 9;
      for (let k = 0; k < n; k++) {
        const t0 = k / n;
        const t1 = (k + 1) / n;
        const a = tmpV.copy(top).lerp(anchor, t0);
        a.y -= Math.sin(Math.PI * t0) * 0.35;
        const b = top.clone().lerp(anchor, t1);
        b.y -= Math.sin(Math.PI * t1) * 0.35;
        const mid = a.clone().add(b).multiplyScalar(0.5);
        const len = a.distanceTo(b);
        const dir = b.clone().sub(a).normalize();
        const qq = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir);
        kit.add(box, '#2a2622', new THREE.Matrix4().compose(mid, qq, new THREE.Vector3(len, 0.012, 0.012)));
        if (k > 0) {
          const yawF = Math.atan2(dir.x, dir.z) + Math.PI / 2;
          kit.add(flag, FLAGS[(k + i) % FLAGS.length] as string, place(a.x, a.y - 0.07, a.z, yawF, new THREE.Vector3(0.11, 0.14, 1)));
        }
      }
    });

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0, flatShading: true });
    const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, litMat);
    for (const [k, m, name] of [
      [kit, mat, 'fair-square'],
      [lit, litMat, 'fair-lamps'],
    ] as const) {
      const mesh = k.build(m, name);
      if (!mesh) continue;
      mesh.receiveShadow = m === mat;
      mesh.castShadow = m === mat && !ctx.low;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // Where each lantern's light ends on the flags: a faint warm rim (the thieves show only inside).
    const rimKit = new Kit();
    const rim = new THREE.RingGeometry(0.96, 1, 48).rotateX(-Math.PI / 2);
    for (const st of this.stalls) rimKit.add(rim, '#ffd6a0', place(st.p.x, ctx.ground(st.p.x, st.p.z) + 0.035, st.p.z, 0, new THREE.Vector3(this.light, 1, this.light)));
    const rimMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.14, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mats.push(rimMat);
    const rims = rimKit.build(rimMat, 'fair-pools');
    if (rims) {
      rims.renderOrder = 2;
      this.geos.push(rims.geometry);
      this.group.add(rims);
    }

    // The wares: one instanced mesh per shape, each ware an instance with its own colour.
    const shapes: Record<Shape, THREE.BufferGeometry> = {
      0: new THREE.BoxGeometry(0.2, 0.16, 0.18).translate(0, 0.08, 0),
      1: new THREE.DodecahedronGeometry(0.11, 0).scale(1, 1.15, 0.9).translate(0, 0.11, 0),
      2: new THREE.CylinderGeometry(0.06, 0.09, 0.2, 9).translate(0, 0.1, 0),
      3: new THREE.CylinderGeometry(0.06, 0.06, 0.26, 9).rotateZ(Math.PI / 2).translate(0, 0.06, 0),
      4: new THREE.CylinderGeometry(0.11, 0.11, 0.08, 12).translate(0, 0.04, 0),
    };
    const counts: number[] = [0, 0, 0, 0, 0];
    const perStall = new Map<number, number>();
    const order: { stall: number; shape: Shape; k: number }[] = [];
    for (const s0 of r.stalls.keys()) for (let k = 0; k < (r.stalls[s0]?.wares ?? 0); k++) {
      const shape = (s0 === r.cart ? 2 : (s0 * 2 + k) % 5) as Shape;
      order.push({ stall: s0, shape, k });
    }
    for (const o of order) {
      const at = perStall.get(o.stall) ?? 0;
      perStall.set(o.stall, at + 1);
      const slot = this.stalls[o.stall]?.slots[at % 2] ?? new THREE.Vector3();
      this.wares.push({ shape: o.shape, i: counts[o.shape] as number, slot });
      counts[o.shape] = (counts[o.shape] as number) + 1;
    }
    const wareMat = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0, flatShading: true });
    this.mats.push(wareMat);
    const col = new THREE.Color();
    for (let sh = 0; sh < 5; sh++) {
      const n = counts[sh] as number;
      const geo = shapes[sh as Shape];
      this.geos.push(geo);
      const im = new THREE.InstancedMesh(geo, wareMat, Math.max(1, n));
      im.count = n;
      im.frustumCulled = false;
      im.castShadow = !ctx.low;
      let c = 0;
      this.wares.forEach((w, wi) => {
        if (w.shape !== sh) return;
        const list = WARE_COLORS[w.shape];
        im.setColorAt(c++, col.set(list[(wi + w.i) % list.length] as string));
      });
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      this.meshes.push(im);
      this.group.add(im);
    }

    // The cold rings under wares lying loose.
    this.ringMat = new THREE.MeshBasicMaterial({ color: COOL.clone(), transparent: true, opacity: 0.6, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mats.push(this.ringMat);
    const rg = new THREE.RingGeometry(0.26, 0.34, 32).rotateX(-Math.PI / 2);
    this.geos.push(rg);
    this.rings = new THREE.InstancedMesh(rg, this.ringMat, Math.max(1, this.wares.length));
    this.rings.count = 0;
    this.rings.renderOrder = 2;
    this.rings.frustumCulled = false;
    this.group.add(this.rings);

    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, ringG, flag, rim]) g.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** Every fresh tick: the events' small effects, and the flare when the fair closes. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    for (const e of s._events ?? []) {
      if (e.t !== 'fair') continue;
      const p = this.ctx.at(e.x, e.y, 0.3);
      if (e.kind === 'back') {
        const st = this.stalls[e.stall];
        const at = st ? st.p : p;
        this.ctx.vfx.ring(this.v.set(at.x, this.ctx.ground(at.x, at.z) + 0.06, at.z), 0.2, 1.2, '#cfe8ff', 10, 0.7);
        this.ctx.vfx.burst(this.v.set(at.x, at.y + 0.7, at.z), { n: low ? 4 : 9, color: '#e8f4ff', speed: 0.03, up: 0.04, size: 0.04, life: 16, gravity: 0.004, spread: 0.3 });
      } else if (e.kind === 'lost') {
        this.ctx.vfx.burst(p, { n: low ? 6 : 14, color: '#0c0914', speed: 0.05, up: 0.04, size: 0.08, life: 22, gravity: 0.006, spread: 0.4 });
        this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#2a2438', speed: 0.02, up: 0.03, size: 0.45, life: 30, smoke: true, spread: 0.4 });
      } else if (e.kind === 'drop') {
        this.ctx.vfx.burst(p, { n: low ? 3 : 7, color: '#8a8070', speed: 0.03, up: 0.02, size: 0.06, life: 14, gravity: 0.006, spread: 0.25 });
      } else if (e.kind === 'taken') {
        this.ctx.vfx.burst(p, { n: low ? 2 : 5, color: '#1a1228', speed: 0.02, up: 0.03, size: 0.06, life: 14, gravity: 0.004, spread: 0.2 });
      } else if (e.kind === 'end') {
        this.flaredAt = this.now;
        for (const st of this.stalls) {
          this.ctx.flare(st.lamp.clone().setY(st.lamp.y + 0.1), '#ffd6a0', 2.4, 16);
          this.ctx.vfx.burst(st.lamp, { n: low ? 4 : 10, color: '#ffe2b8', speed: 0.03, up: 0.05, size: 0.045, life: 24, gravity: -0.001, spread: 0.3 });
        }
      }
    }
  }

  /** Every redraw: each ware where it is, the rings under loose ones, the lanterns and their pools. */
  draw(s: BattleState): void {
    const st = s.fair;
    const ws = st?.wares ?? [];
    let rn = 0;
    const pulse = 0.5 + 0.5 * Math.sin(this.now * 4);
    for (let wi = 0; wi < this.wares.length; wi++) {
      const w = this.wares[wi] as (typeof this.wares)[number];
      const sw = ws[wi];
      const im = this.meshes[w.shape] as THREE.InstancedMesh;
      const at = sw?.at ?? 'stall';
      if (at === 'lost') {
        im.setMatrixAt(w.i, this.zero);
        continue;
      }
      let yaw = wi * 1.3;
      let tilt = 0;
      if (at === 'stall' || !sw) this.v.copy(w.slot);
      else if (at === 'carried') {
        const h = s.heroes.find((x) => x.pid === sw.pid);
        const hp = h ? this.ctx.pos(h.id) : undefined;
        if (hp) this.v.copy(hp);
        else this.v.copy(this.ctx.at(sw.x, sw.y, 0));
        this.v.y += 1.95;
        yaw = this.now * 0.8;
      } else if (at === 'dragged') {
        // At the dragger's heels: a little behind it, the way it is going.
        const dp = this.ctx.pos(sw.by);
        if (dp) this.v.copy(dp);
        else this.v.copy(this.ctx.at(sw.x, sw.y, 0));
        const e = s.enemies.find((x) => x.id === sw.by);
        if (e) {
          const a = (e.facing / 1024) * Math.PI * 2;
          const here = this.ctx.at(e.x, e.y, 0);
          const back = this.ctx.at(e.x - Math.cos(a) * 380, e.y - Math.sin(a) * 380, 0);
          this.v.x += back.x - here.x;
          this.v.z += back.z - here.z;
        }
        this.v.y = this.ctx.ground(this.v.x, this.v.z) + 0.06;
        tilt = 0.35;
        yaw = this.now * 0.3 + wi;
      } else {
        this.v.copy(this.ctx.at(sw.x, sw.y, 0));
        this.v.y = this.ctx.ground(this.v.x, this.v.z);
        tilt = 0.25;
        this.rings.setMatrixAt(rn++, this.m.compose(this.sv.set(this.v.x, this.v.y + 0.04, this.v.z), this.q.identity(), this.sv2(0.95 + 0.25 * pulse)));
      }
      this.q.setFromEuler(this.e.set(tilt, yaw, 0, 'YXZ'));
      const k = at === 'carried' ? 1.9 : 1.6;
      im.setMatrixAt(w.i, this.m.compose(this.v, this.q, this.sv.set(k, k, k)));
    }
    for (const im of this.meshes) im.instanceMatrix.needsUpdate = true;
    this.rings.count = rn;
    this.rings.instanceMatrix.needsUpdate = true;
    this.ringMat.opacity = 0.35 + 0.35 * pulse;

    this.glow.begin();
    const flare = this.flaredAt >= 0 ? Math.max(0, 1 - (this.now - this.flaredAt) / 2.5) : 0;
    for (const [i, stl] of this.stalls.entries()) {
      const f = 0.9 + 0.1 * Math.sin(this.now * 3 + i * 1.7);
      this.glow.add(stl.lamp.x, stl.lamp.y, stl.lamp.z, flare > 0 ? LAMP_HOT : LAMP, 0.55 + 0.6 * flare, (0.75 + 0.25 * flare) * f);
      this.glow.add(stl.p.x, this.ctx.ground(stl.p.x, stl.p.z) + 0.04, stl.p.z, LAMP, this.light * 2.1, (0.13 + 0.12 * flare) * f);
    }
    // A loose ware lies glinting.
    for (const sw of ws) {
      if (sw.at !== 'ground') continue;
      const p = this.ctx.at(sw.x, sw.y, 0.2);
      this.glow.add(p.x, p.y, p.z, COOL, 0.7, 0.35 + 0.25 * pulse);
    }
    // Each standing hero's own lantern: a smaller warm pool.
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      const hp = this.ctx.pos(h.id);
      if (!hp) continue;
      this.glow.add(hp.x, this.ctx.ground(hp.x, hp.z) + 0.04, hp.z, LAMP, this.lantern * 2.0, 0.08);
    }
    this.glow.end();
  }

  private sv2v = new THREE.Vector3();
  private sv2(k: number): THREE.Vector3 {
    return this.sv2v.set(k, 1, k);
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
